if (!customElements.get('sc-contact-form-form')) {
  customElements.define(
    'sc-contact-form-form',
    class ScContactForm extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;

        this.initialized = true;
        this.form = this.querySelector('form');
        this.topicInput = this.form?.elements.namedItem('contact[topic]');
        this.nameInput = this.form?.elements.namedItem('contact[name]');
        this.orderInput = this.form?.elements.namedItem('contact[order]');
        this.emailInput = this.form?.elements.namedItem('contact[email]');
        this.messageInput = this.form?.elements.namedItem('contact[message]');
        this.fileInput = this.querySelector('[data-sc-contact-form-file]');
        this.dropzone = this.querySelector('[data-sc-contact-form-dropzone]');
        this.fileSelection = this.querySelector('[data-sc-contact-form-file-selection]');
        this.fileName = this.querySelector('[data-sc-contact-form-file-name]');
        this.fileMeta = this.querySelector('[data-sc-contact-form-file-meta]');
        this.fileError = this.querySelector('[data-sc-contact-form-file-error]');
        this.removeButton = this.querySelector('[data-sc-contact-form-file-remove]');
        this.statusMessage = this.querySelector('[data-sc-contact-form-status]');
        this.submitButton = this.querySelector('[data-sc-contact-form-submit]');

        this.fileInput?.addEventListener('change', () => this.handleFileChange());
        this.removeButton?.addEventListener('click', () => this.clearFile());

        [this.topicInput, this.nameInput, this.orderInput, this.emailInput, this.messageInput]
          .filter(Boolean)
          .forEach((input) => {
            const clearError = () => {
              input.setCustomValidity('');
              this.clearErrorStatus();
            };
            input.addEventListener('input', clearError);
            input.addEventListener('change', clearError);
          });

        ['dragenter', 'dragover'].forEach((eventName) => {
          this.dropzone?.addEventListener(eventName, (event) => this.handleDragOver(event));
        });

        ['dragleave', 'dragend', 'drop'].forEach((eventName) => {
          this.dropzone?.addEventListener(eventName, (event) => this.handleDragEnd(event));
        });

        this.dropzone?.addEventListener('drop', (event) => this.handleDrop(event));
        this.form?.addEventListener('submit', (event) => this.submitForm(event), true);
        this.waitForShopifyFormsBridge()
          .then((bridge) => this.syncTopicOptions(bridge.topicInput))
          .catch(() => {});
      }

      syncTopicOptions(nativeSelect) {
        if (!this.topicInput || !(nativeSelect instanceof HTMLSelectElement)) return;

        const nativeOptions = Array.from(nativeSelect.options)
          .filter((option) => option.value && !option.disabled);
        if (!nativeOptions.length) return;

        const selectedLabel = this.topicInput.selectedOptions[0]?.textContent.trim();
        const placeholder = this.topicInput.options[0];
        const options = nativeOptions.map((option) => new Option(option.textContent.trim(), option.value));
        this.topicInput.replaceChildren(placeholder, ...options);

        const selectedOption = options.find((option) => option.textContent.trim() === selectedLabel);
        this.topicInput.value = selectedOption?.value || '';
      }

      handleDragOver(event) {
        event.preventDefault();
        event.stopPropagation();
        this.dropzone?.classList.add('is-dragover');

        if (event.dataTransfer) {
          event.dataTransfer.dropEffect = 'copy';
        }
      }

      handleDragEnd(event) {
        event.preventDefault();
        event.stopPropagation();
        this.dropzone?.classList.remove('is-dragover');
      }

      handleDrop(event) {
        const file = event.dataTransfer?.files?.[0];

        if (!file || !this.fileInput) return;

        try {
          const transfer = new DataTransfer();
          transfer.items.add(file);
          this.fileInput.files = transfer.files;
          this.handleFileChange();
        } catch (error) {
          console.error('[sc-contact-form] Dropped file could not be selected', error);
          this.setFileError(this.fileInput.dataset.typeError || 'Die Datei konnte nicht ausgewählt werden.');
        }
      }

      handleFileChange() {
        const file = this.fileInput?.files?.[0];

        this.setFileError('');
        this.clearErrorStatus();

        if (!file) {
          this.updateFileSelection(null);
          return;
        }

        const validationMessage = this.validateFile(file);

        if (validationMessage) {
          this.setFileError(validationMessage);
          this.updateFileSelection(null);
          return;
        }

        this.updateFileSelection(file);
      }

      validateFile(file) {
        const maxSize = Number(this.fileInput?.dataset.maxSize || 0);
        const acceptedTypes = String(this.fileInput?.accept || '')
          .split(',')
          .map((value) => value.trim().toLowerCase())
          .filter(Boolean);

        if (maxSize && file.size > maxSize) {
          return this.fileInput?.dataset.sizeError || 'Die Datei ist zu groß.';
        }

        if (acceptedTypes.length && !this.isAcceptedFile(file, acceptedTypes)) {
          return this.fileInput?.dataset.typeError || 'Dieser Dateityp wird nicht unterstützt.';
        }

        return '';
      }

      isAcceptedFile(file, acceptedTypes) {
        const fileName = file.name.toLowerCase();
        const mimeType = file.type.toLowerCase();

        return acceptedTypes.some((acceptedType) => {
          if (acceptedType.startsWith('.')) return fileName.endsWith(acceptedType);
          if (acceptedType.endsWith('/*')) return mimeType.startsWith(acceptedType.slice(0, -1));
          return mimeType === acceptedType;
        });
      }

      getFileExtension(file) {
        const fileName = String(file?.name || '');
        const dotIndex = fileName.lastIndexOf('.');

        return dotIndex > 0 ? fileName.slice(dotIndex + 1).toLowerCase() : '';
      }

      getFileKind(file) {
        const extension = this.getFileExtension(file);
        const mimeType = String(file?.type || '').toLowerCase();

        if (mimeType === 'application/pdf' || extension === 'pdf') return 'pdf';

        if (
          mimeType.startsWith('image/')
          || ['avif', 'bmp', 'gif', 'heic', 'heif', 'jpeg', 'jpg', 'png', 'svg', 'webp'].includes(extension)
        ) {
          return 'image';
        }

        if (
          mimeType.startsWith('text/')
          || ['doc', 'docx', 'odt', 'rtf', 'txt'].includes(extension)
        ) {
          return 'text';
        }

        return 'generic';
      }

      formatFileSize(bytes) {
        const size = Number(bytes || 0);

        if (size < 1024) return `${size} B`;

        const units = ['KB', 'MB', 'GB'];
        const unitIndex = Math.min(Math.floor(Math.log(size) / Math.log(1024)) - 1, units.length - 1);
        const value = size / (1024 ** (unitIndex + 1));
        const formatter = new Intl.NumberFormat(document.documentElement.lang || 'de-DE', {
          maximumFractionDigits: value >= 10 ? 0 : 1
        });

        return `${formatter.format(value)} ${units[unitIndex]}`;
      }

      getFileMeta(file) {
        const extension = this.getFileExtension(file);
        const type = extension ? extension.toUpperCase() : 'DATEI';

        return `${type} · ${this.formatFileSize(file?.size)}`;
      }

      updateFileSelection(file) {
        if (this.fileName) this.fileName.textContent = file?.name || '';
        if (this.fileMeta) this.fileMeta.textContent = file ? this.getFileMeta(file) : '';
        if (this.fileSelection) this.fileSelection.hidden = !file;
        this.dropzone?.classList.toggle('has-file', Boolean(file));

        if (this.dropzone) {
          if (file) {
            this.dropzone.dataset.fileKind = this.getFileKind(file);
          } else {
            delete this.dropzone.dataset.fileKind;
          }
        }
      }

      setFileError(message) {
        this.fileInput?.setCustomValidity(message);

        if (this.fileError) {
          this.fileError.textContent = message;
          this.fileError.hidden = !message;
        }
      }

      clearFile() {
        if (this.fileInput) this.fileInput.value = '';
        this.setFileError('');
        this.updateFileSelection(null);
        this.clearErrorStatus();
        this.fileInput?.focus();
      }

      async submitForm(event) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        if (!this.form || this.form.dataset.isSubmitting === 'true') return;

        this.handleFileChange();

        if (!this.form.checkValidity()) {
          this.form.reportValidity();
          return;
        }

        const defaultSubmitLabel = this.submitButton?.textContent || '';
        const loadingLabel = this.statusMessage?.dataset.loadingLabel || 'Wird gesendet...';
        const topic = this.topicInput?.value.trim() || '';
        const name = this.nameInput?.value.trim() || '';
        const order = this.orderInput?.value.trim() || '';
        const email = this.emailInput?.value.trim() || '';
        const message = this.messageInput?.value.trim() || '';
        const file = this.fileInput?.files?.[0] || null;
        let restoreShopifyFormsScroll = null;
        let submittedSuccessfully = false;

        this.setStatus('', '');
        this.form.dataset.isSubmitting = 'true';
        this.toggleSubmitting(true);

        if (this.submitButton) this.submitButton.textContent = loadingLabel;

        try {
          const bridge = await this.waitForShopifyFormsBridge();

          this.validateShopifyFormsBridge(bridge);
          this.activeBridge = bridge;
          if (topic) await this.setShopifyFormsTopicValue(bridge, topic);
          this.setNativeValue(bridge.nameInput, name);
          this.setNativeValue(bridge.orderInput, order);
          this.setNativeValue(bridge.emailInput, email);
          this.setNativeValue(bridge.messageInput, message);
          if (file) this.setNativeFile(bridge.fileInput, file);
          restoreShopifyFormsScroll = this.preventShopifyFormsAutoScroll();
          this.submitShopifyFormsBridge(bridge);
          await this.waitForShopifyFormsResult(bridge);

          this.setStatus(
            this.statusMessage?.dataset.successMessage || 'Vielen Dank für deine Nachricht. Wir melden uns bei dir.',
            'success'
          );
          this.form.reset();
          this.setFileError('');
          this.updateFileSelection(null);
          submittedSuccessfully = true;
        } catch (error) {
          console.error('[sc-contact-form] Shopify Forms bridge submit failed', error);

          if (!this.applyShopifyFormsFieldErrors(error)) {
            this.setStatus(
              this.statusMessage?.dataset.errorMessage || 'Die Nachricht konnte nicht gesendet werden. Bitte versuche es erneut.',
              'error'
            );
          }
        } finally {
          restoreShopifyFormsScroll?.();
          this.form.dataset.isSubmitting = 'false';
          this.toggleSubmitting(false);

          if (this.submitButton) this.submitButton.textContent = defaultSubmitLabel;
          if (submittedSuccessfully) window.requestAnimationFrame(() => this.scrollToSuccess());
        }
      }

      scrollToSuccess() {
        const section = this.getSectionScope();
        const target = section?.getBoundingClientRect().height <= window.innerHeight
          ? section
          : this.statusMessage;

        target?.scrollIntoView({
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
          block: 'center'
        });
      }

      getSectionScope() {
        return this.closest('.sc-contact-form-section') || this.closest('.shopify-section');
      }

      findCustomField(root, selector, match) {
        if (!root) return null;

        return Array.from(root.querySelectorAll(selector)).find((field) => {
          const label = field.labels?.[0]?.textContent || '';
          const containerText = field.closest('[class*="formFieldContainer"], [class*="formFileInput"]')?.textContent || '';
          const fieldText = `${field.id} ${field.name} ${field.dataset.testid || ''} ${field.getAttribute('aria-labelledby') || ''} ${label} ${containerText}`;
          return match.test(fieldText.toLowerCase());
        }) || null;
      }

      waitForShopifyFormsBridge(timeout = 15000) {
        const startedAt = Date.now();

        return new Promise((resolve, reject) => {
          const findBridge = () => {
            const scope = this.getSectionScope();
            const embed = scope?.querySelector('shopify-forms-embed#app-embed')
              || scope?.querySelector('shopify-forms-embed');
            const root = embed?.shadowRoot;
            const appRoot = root?.querySelector('#app-embed[data-current-step]')
              || root?.querySelector('[data-current-step]');
            const form = root?.querySelector('form[data-testid="form"]');
            const topicInput = this.findCustomField(root, 'select, button[aria-labelledby]', /thema/);
            const nameInput = root?.getElementById('first_name');
            const orderInput = this.findCustomField(root, 'input:not([type="file"])', /lieferschein|bestellnummer/);
            const emailInput = root?.getElementById('email');
            const messageInput = this.findCustomField(root, 'textarea', /nachricht/);
            const fileInput = this.findCustomField(root, 'input[type="file"]', /datei|upload/);
            const submitButton = root?.querySelector('[data-testid="btn-form-submit"]')
              || form?.querySelector('button[type="submit"], input[type="submit"]');

            if (
              embed && root && appRoot && form && topicInput && nameInput && orderInput
              && emailInput && messageInput && fileInput && submitButton
            ) {
              return {
                scope,
                embed,
                root,
                appRoot,
                form,
                topicInput,
                nameInput,
                orderInput,
                emailInput,
                messageInput,
                fileInput,
                submitButton
              };
            }

            return null;
          };

          const tick = () => {
            const bridge = findBridge();

            if (bridge) {
              resolve(bridge);
              return;
            }

            if (Date.now() - startedAt >= timeout) {
              reject(new Error('Shopify Forms contact bridge was not found or mounted in time.'));
              return;
            }

            window.setTimeout(tick, 150);
          };

          tick();
        });
      }

      validateShopifyFormsBridge(bridge) {
        if (!bridge.scope || !bridge.embed || !bridge.scope.contains(bridge.embed)) {
          throw new Error('Shopify Forms bridge is not mounted inside the contact section.');
        }

        const requiredInputs = [
          bridge.topicInput,
          bridge.nameInput,
          bridge.orderInput,
          bridge.emailInput,
          bridge.messageInput,
          bridge.fileInput
        ];

        if (requiredInputs.some((input) => !input)) {
          throw new Error('One or more Shopify Forms contact inputs are missing.');
        }

        if (!bridge.submitButton || bridge.submitButton.disabled || bridge.submitButton.getAttribute('aria-disabled') === 'true') {
          throw new Error('Shopify Forms submit button is not available.');
        }
      }

      setNativeValue(input, value) {
        if (!input) return;

        const prototype = input instanceof HTMLTextAreaElement
          ? HTMLTextAreaElement.prototype
          : input instanceof HTMLSelectElement
            ? HTMLSelectElement.prototype
            : HTMLInputElement.prototype;
        const descriptor = Object.getOwnPropertyDescriptor(prototype, 'value');

        if (descriptor?.set) {
          descriptor.set.call(input, value);
        } else {
          input.value = value;
        }

        input.dispatchEvent(new Event('input', { bubbles: true, composed: true }));
        input.dispatchEvent(new Event('change', { bubbles: true, composed: true }));
      }

      async setShopifyFormsTopicValue(bridge, value) {
        const control = bridge.topicInput;

        if (control instanceof HTMLSelectElement) {
          const option = Array.from(control.options)
            .find((item) => item.value === value || item.textContent.trim() === value);
          if (!option) throw new Error(`Shopify Forms does not offer the selected topic: ${value}`);
          this.setNativeValue(control, option.value);
          return;
        }

        control.click();

        for (let attempt = 0; attempt < 20; attempt += 1) {
          if (control.textContent.trim() === value) return;

          const option = Array.from(bridge.root.querySelectorAll('[role="option"]'))
            .find((item) => item.textContent.trim() === value);

          if (option) {
            option.click();
            if (control.textContent.trim() === value) return;
          }

          await new Promise((resolve) => window.setTimeout(resolve, 50));
        }

        throw new Error(`Shopify Forms did not select the topic: ${value}`);
      }

      setNativeFile(input, file) {
        if (!input || !file) return;

        const transfer = new DataTransfer();
        transfer.items.add(file);

        input.files = transfer.files;
        input.dispatchEvent(new Event('input', { bubbles: true, composed: true }));
        input.dispatchEvent(new Event('change', { bubbles: true, composed: true }));
      }

      submitShopifyFormsBridge(bridge) {
        if (bridge.form.requestSubmit) {
          bridge.form.requestSubmit(bridge.submitButton);
          return;
        }

        bridge.submitButton.click();
      }

      preventShopifyFormsAutoScroll() {
        const originalScrollTo = window.scrollTo;
        const form = this.form;

        const guardedScrollTo = function (...args) {
          if (form?.dataset.isSubmitting === 'true' && args[0]?.behavior === 'smooth') return;
          return originalScrollTo.apply(this, args);
        };

        window.scrollTo = guardedScrollTo;

        return () => {
          if (window.scrollTo === guardedScrollTo) window.scrollTo = originalScrollTo;
        };
      }

      waitForShopifyFormsResult(bridge, timeout = 30000) {
        const startedAt = Date.now();
        const errorSettleDelay = 900;

        return new Promise((resolve, reject) => {
          let observer = null;
          let timeoutId = null;
          let errorCheckId = null;
          let completed = false;
          let firstErrorSeenAt = 0;
          let pendingFieldErrors = {};

          const cleanup = () => {
            completed = true;
            observer?.disconnect();
            if (timeoutId) window.clearTimeout(timeoutId);
            if (errorCheckId) window.clearTimeout(errorCheckId);
          };

          const finishResolve = () => {
            if (completed) return true;
            cleanup();
            resolve();
            return true;
          };

          const finishReject = (error) => {
            if (completed) return true;
            cleanup();
            reject(error);
            return true;
          };

          const scheduleErrorRecheck = () => {
            if (errorCheckId) return;

            const elapsed = Date.now() - firstErrorSeenAt;
            const delay = Math.max(50, errorSettleDelay - elapsed + 25);

            errorCheckId = window.setTimeout(() => {
              errorCheckId = null;
              checkState();
            }, delay);
          };

          const checkState = () => {
            if (completed) return true;

            const currentAppRoot = bridge.root.querySelector('#app-embed[data-current-step]')
              || bridge.root.querySelector('[data-current-step]')
              || bridge.appRoot;
            const currentStep = currentAppRoot?.getAttribute('data-current-step');

            if (currentStep === 'success') return finishResolve();

            const fieldErrors = this.collectShopifyFormsFieldErrors(bridge);

            if (Object.keys(fieldErrors).length) {
              pendingFieldErrors = fieldErrors;
              firstErrorSeenAt = firstErrorSeenAt || Date.now();

              if (Date.now() - firstErrorSeenAt >= errorSettleDelay) {
                const error = new Error('Shopify Forms reported validation errors.');
                error.fieldErrors = pendingFieldErrors;
                return finishReject(error);
              }

              scheduleErrorRecheck();
              return false;
            }

            firstErrorSeenAt = 0;
            pendingFieldErrors = {};

            if (Date.now() - startedAt >= timeout) {
              return finishReject(new Error('Shopify Forms did not report a submit result in time.'));
            }

            return false;
          };

          if (checkState()) return;

          observer = new MutationObserver(() => checkState());
          observer.observe(bridge.root, { attributes: true, childList: true, subtree: true });

          timeoutId = window.setTimeout(() => checkState(), timeout + 50);
        });
      }

      collectShopifyFormsFieldErrors(bridge) {
        const invalidFields = Array.from(
          bridge.root.querySelectorAll('input[aria-invalid="true"], textarea[aria-invalid="true"], select[aria-invalid="true"]')
        );

        const fieldErrors = invalidFields.reduce((errors, field) => {
          const fieldName = field.name || field.id || field.dataset.testid?.replace(/^field-/, '');

          if (fieldName) errors[fieldName] = this.getShopifyFormsFieldErrorMessage(field) || '';
          return errors;
        }, {});

        if (!Object.keys(fieldErrors).length) {
          const generalErrors = Array.from(
            bridge.root.querySelectorAll('[role="alert"], [data-testid*="error"]')
          )
            .map((element) => element.textContent?.trim())
            .filter(Boolean);

          if (generalErrors.length) fieldErrors.general = generalErrors.join(' ');
        }

        return fieldErrors;
      }

      getShopifyFormsFieldErrorMessage(field) {
        const describedBy = field.getAttribute('aria-describedby');

        if (describedBy) {
          const describedByElement = field.getRootNode()?.getElementById?.(describedBy);
          const describedByText = describedByElement?.textContent?.trim();
          if (describedByText) return describedByText;
        }

        const container = field.closest(
          '[class*="formFieldContainer"], [class*="formPhoneInputContainer"], [class*="formFileInput"]'
        );
        const errorElement = container?.querySelector(
          '[role="alert"], [data-testid*="error"], [class*="error"], [class*="Error"]'
        );

        return errorElement?.textContent?.trim() || '';
      }

      applyShopifyFormsFieldErrors(error) {
        const fieldErrors = error?.fieldErrors || {};
        const bridge = this.activeBridge;
        const mappings = [
          { native: bridge?.nameInput, input: this.nameInput },
          { native: bridge?.emailInput, input: this.emailInput },
          { native: bridge?.topicInput, input: this.topicInput },
          { native: bridge?.orderInput, input: this.orderInput },
          { native: bridge?.messageInput, input: this.messageInput }
        ];

        for (const mapping of mappings) {
          const key = [mapping.native?.name, mapping.native?.id]
            .find((item) => item && Object.prototype.hasOwnProperty.call(fieldErrors, item));

          if (key && mapping.input) {
            mapping.input.setCustomValidity(fieldErrors[key] || 'Bitte überprüfen Sie dieses Feld.');
            mapping.input.reportValidity();
            return true;
          }
        }

        const fileKey = [
          bridge?.fileInput?.name,
          bridge?.fileInput?.id,
          bridge?.fileInput?.id?.replace(/-field$/, '')
        ]
          .find((item) => Object.prototype.hasOwnProperty.call(fieldErrors, item));

        if (fileKey && this.fileInput) {
          this.setFileError(fieldErrors[fileKey] || 'Bitte überprüfen Sie die ausgewählte Datei.');
          return true;
        }

        return false;
      }

      clearErrorStatus() {
        if (this.statusMessage?.classList.contains('sc-contact-form-form__message--error')) {
          this.setStatus('', '');
        }
      }

      setStatus(message, type) {
        if (!this.statusMessage) return;

        const hasMessage = Boolean(message);

        this.statusMessage.textContent = message;
        this.statusMessage.hidden = !hasMessage;
        this.statusMessage.classList.toggle('sc-contact-form-form__message--success', type === 'success');
        this.statusMessage.classList.toggle('sc-contact-form-form__message--error', type === 'error');
        this.classList.toggle('is-success', hasMessage && type === 'success');
      }

      toggleSubmitting(isSubmitting) {
        if (!this.submitButton) return;

        this.submitButton.disabled = isSubmitting;
        this.submitButton.classList.toggle('is-loading', isSubmitting);
      }
    }
  );
}
